import React from 'react';
import CardSection from './CardSection';
import {
  Block,
  BlockTitle,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Col,
  Link,
  Row
} from 'framework7-react';
import PropTypes from 'prop-types';
import { Elements, StripeProvider } from 'react-stripe-elements';
import SepaForm from './SepaForm';
import Wallet from './Wallet';
import II18 from '../translate/II18';
import { gae, gaPageView } from '../helpers/Analytics';
import StripeScriptLoader from './StripeScriptLoader';
import Checkmark from '../helpers/Checkmark';

const PAYMENT_CARD = 1;
const PAYMENT_SEPA = 2;
// eslint-disable-next-line no-unused-vars
const PAYMENT_BANK = 3;
const PAYMENT_WALLET = 4;

const PAYMENT_TYPE_VISA = 'VISA';
const PAYMENT_TYPE_MASTERCARD = 'MASTERCARD';
const PAYMENT_TYPE_MAESTRO = 'MAESTRO';
const PAYMENT_TYPE_AMEX = 'AMEX';
const PAYMENT_TYPE_APPLE_PAY = 'APPLE_PAY';
const PAYMENT_TYPE_GOOGLE_PAY = 'GOOGLE_PAY';
// eslint-disable-next-line no-unused-vars
const PAYMENT_TYPE_GIROPAY = 'GIROPAY';
// eslint-disable-next-line no-unused-vars
const PAYMENT_TYPE_SOFORT = 'SOFORT';
const PAYMENT_TYPE_SEPA = 'SEPA';

class PaymentContent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedPayment: 0,
      showAll: false,
      justPayed: false
    };
  }

  componentDidMount() {
    gaPageView('Payment');
  }

  setPayment = payment => {
    gae('PAYMENT', 'SELECT_PAYMENT_TYPE', payment);
    this.setState({
      selectedPayment: payment
    });
  };

  getPaymentTitle = payment => {
    switch (payment) {
      case PAYMENT_CARD:
        return (
          <span>
            <i className="far fa-credit-card" /> <II18>payment_creditcard</II18>
          </span>
        );
      case PAYMENT_SEPA:
        return (
          <span>
            <i className="fas fa-money-check" /> <II18>payment_sepa</II18>
          </span>
        );
      case PAYMENT_WALLET:
        return (
          <span>
            <i className="fas fa-wallet" /> <II18>payment_wallet</II18>
          </span>
        );
      //no default
    }
  };

  goBack = () => {
    if (this.state.selectedPayment && !this.state.justPayed) {
      this.setPayment(0);
    } else {
      this.props.navigateBack();
    }
  };

  handleSubmit = ev => {
    ev.preventDefault();
    //const cardElement = this.props.elements.getElement('card');
  };

  onPaymentSuccess = method => {
    gae('PAYMENT', 'PAYMENT_SUCCESS');

    if (this.props.isSkipDocumentListMode) {
      this.props.onDocumentDone(true);
    }

    this.setState(
      {
        justPayed: true
      },
      () => this.props.setDocumentPayed(this.props.document.id, method)
    );
  };

  onPaymentError = code => {
    gae('PAYMENT', 'PAYMENT_ERROR');

    this.$f7.dialog.alert(
      code ? code : II18.get('payment_error_message'),
      II18.get('payment_error_title')
    );
  };

  render() {
    const { document, payed } = this.props;

    const { showAll } = this.state;
    const {
      amount,
      currency,
      stripeClientSecret,
      paymentMethodTypes,
      paymentPurpose
    } = document.attributes;

    const formattedAmount = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);

    const message = paymentPurpose !== undefined ? paymentPurpose : '';
    const selectedPayment = this.state.selectedPayment;

    const cardEnabled =
      paymentMethodTypes.includes(PAYMENT_TYPE_VISA) ||
      paymentMethodTypes.includes(PAYMENT_TYPE_MASTERCARD) ||
      paymentMethodTypes.includes(PAYMENT_TYPE_MAESTRO) ||
      paymentMethodTypes.includes(PAYMENT_TYPE_AMEX);

    const sepaEnabled = paymentMethodTypes.includes(PAYMENT_TYPE_SEPA);
    const walletEnabled =
      (paymentMethodTypes.includes(PAYMENT_TYPE_APPLE_PAY) &&
        this.$f7.device.ios) ||
      (paymentMethodTypes.includes(PAYMENT_TYPE_GOOGLE_PAY) &&
        this.$f7.device.android);

    const isPaymentProcessing = document.state === 'PROCESSING';
    const stripeApiKey = process.env.REACT_APP_STRIPE_APIKEY;

    return (
      <StripeScriptLoader
        uniqueId="loadStripe"
        script="https://js.stripe.com/v3/"
      >
        <StripeProvider apiKey={stripeApiKey}>
          <>
            <Card title={document.name}>
              {((!selectedPayment && message) || payed) && (
                <CardContent>
                  <>
                    {payed && <Checkmark done={!this.state.justPayed} />}

                    {!payed && message && (
                      <p
                        onClick={() =>
                          message.length > 150 &&
                          this.setState({ showAll: !showAll })
                        }
                      >
                        {showAll || message.length < 150
                          ? message
                          : message.slice(0, 150) + '...'}
                      </p>
                    )}
                  </>
                </CardContent>
              )}
              {(!selectedPayment || payed) && (
                <CardFooter>
                  {payed === 'sepa' ? (
                    <p className="small" style={{ color: 'dodgerblue' }}>
                      <II18 params={formattedAmount}>
                        payment_sepa_information
                      </II18>
                    </p>
                  ) : (
                    <p
                      className={
                        'paySumm' +
                        (payed ? ' payed' : '') +
                        (selectedPayment ? ' small' : '')
                      }
                      data-testvalue={amount}
                    >
                      {isPaymentProcessing ? (
                        <span>{II18.get('list_request_processing')}</span>
                      ) : (
                        II18.get(payed ? 'payment_paid' : 'payment_summ') +
                        ': ' +
                        formattedAmount
                      )}
                    </p>
                  )}
                </CardFooter>
              )}
            </Card>

            {!payed &&
              !isPaymentProcessing &&
              (selectedPayment ? (
                <Card>
                  <CardHeader>
                    {this.getPaymentTitle(selectedPayment)}
                  </CardHeader>
                  <CardContent>
                    <Elements>
                      {selectedPayment === PAYMENT_CARD ? (
                        <CardSection
                          amount={amount}
                          currency={currency}
                          stripeClientSecret={stripeClientSecret}
                          onPaymentSuccess={this.onPaymentSuccess}
                          onPaymentError={this.onPaymentError}
                          paymentMethodTypes={paymentMethodTypes}
                        />
                      ) : selectedPayment === PAYMENT_SEPA ? (
                        <SepaForm
                          amount={amount}
                          currency={currency}
                          stripeClientSecret={stripeClientSecret}
                          onPaymentSuccess={this.onPaymentSuccess}
                          onPaymentError={this.onPaymentError}
                        />
                      ) : (
                        <Wallet
                          companyName={this.props.companyName}
                          amount={amount}
                          currency={currency}
                          stripeClientSecret={stripeClientSecret}
                          onPaymentSuccess={this.onPaymentSuccess}
                          onPaymentError={this.onPaymentError}
                        />
                      )}
                    </Elements>
                  </CardContent>
                  <CardFooter style={{ justifyContent: 'flex-end' }}>
                    <Link
                      id={'changePaymentType'}
                      onClick={() => this.setPayment(0)}
                    >
                      <II18>payment_change_payment_method</II18>
                    </Link>
                  </CardFooter>
                </Card>
              ) : (
                <>
                  <BlockTitle>
                    <II18>payment_select_payment_method</II18>
                  </BlockTitle>
                  <Block>
                    <Row>
                      {cardEnabled && (
                        <Col>
                          <Button
                            id={'payment_creditcard'}
                            raised
                            className={'payment-method'}
                            onClick={() => this.setPayment(PAYMENT_CARD)}
                          >
                            <i className="far fa-credit-card" />
                            <br />
                            <II18>payment_creditcard</II18>
                          </Button>
                        </Col>
                      )}

                      {sepaEnabled && (
                        <Col>
                          <Button
                            id={'payment_sepa'}
                            raised
                            className={'payment-method'}
                            onClick={() => this.setPayment(PAYMENT_SEPA)}
                          >
                            <i className="fas fa-money-check" />
                            <br />
                            <span style={{ textTransform: 'uppercase' }}>
                              <II18>payment_sepa</II18>
                            </span>
                          </Button>
                        </Col>
                      )}

                      {walletEnabled && (
                        <Col>
                          <Button
                            id={'payment_wallet'}
                            raised
                            className={'payment-method'}
                            onClick={() => this.setPayment(PAYMENT_WALLET)}
                          >
                            <i className="fas fa-wallet" />
                            <br />
                            <II18>payment_wallet</II18>
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </Block>
                </>
              ))}
          </>
        </StripeProvider>
      </StripeScriptLoader>
    );
  }
}

PaymentContent.propTypes = {
  companyName: PropTypes.string,
  payed: PropTypes.bool,
  setDocumentPayed: PropTypes.func,

  //withDocumentCommonPropTypes
  onDocumentDone: PropTypes.func,
  navigateBack: PropTypes.func,
  renderUserCredential: PropTypes.func,
  renderSender: PropTypes.func,
  scanEnded: PropTypes.bool,
  isSkipDocumentListMode: PropTypes.bool,
  document: PropTypes.object
};

export default PaymentContent;
